import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { ProgressBar } from 'primereact/progressbar';
import axios from 'axios';

const AdminDashboard = () => {
    const API_URL = process.env.REACT_APP_API_PATH;

    // State variables
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [recentBookings, setRecentBookings] = useState([]);
    const [partners, setPartners] = useState([]);
    const [users, setUsers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [partnerPayments, setPartnerPayments] = useState([]);
    const [staff, setStaff] = useState([]);
    const [notifications, setNotifications] = useState([]);
    
    // Filter states
    const [dateRange, setDateRange] = useState(null);
    const [partnerStatus, setPartnerStatus] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [staffRole, setStaffRole] = useState(null);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    
    // Dialog states
    const [showPartnerDialog, setShowPartnerDialog] = useState(false);
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    
    // Chart data
    const [statusChartData, setStatusChartData] = useState(null);
    const [revenueChartData, setRevenueChartData] = useState(null);
    const [chartOptions] = useState({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom'
            }
        }
    });

    // Debug effect
    useEffect(() => {
        console.log('Current stats:', stats);
        console.log('Summary:', stats?.summary);
        console.log('Charts:', stats?.charts);
    }, [stats]);

    useEffect(() => {
        loadDashboardData();
        loadRecentBookings();
        loadNotifications();
    }, []);

    useEffect(() => {
        if (stats?.charts) {
            updateCharts();
        }
    }, [stats]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (dateRange?.[0]) params.append('startDate', dateRange[0]);
            if (dateRange?.[1]) params.append('endDate', dateRange[1]);

            console.log('Fetching from:', `${API_URL}admin/stats?${params.toString()}`);
            
            const response = await fetch(`${API_URL}admin/stats?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const result = await response.json();
            console.log('API Response:', result);
            
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRecentBookings = async () => {
        try {
            const response = await axios.get(`${API_URL}admin/bookings/recent?count=10`);
            if (response.data.success) {
                setRecentBookings(response.data.data);
            }
        } catch (error) {
            console.error('Error loading recent bookings:', error);
        }
    };

    const loadPartners = async (page = 1) => {
        try {
            const response = await axios.get(`${API_URL}admin/partners`, {
                params: {
                    status: partnerStatus,
                    page,
                    pageSize
                }
            });
            if (response.data.success) {
                setPartners(response.data.data);
                setTotalRecords(response.data.pagination.totalCount);
                setCurrentPage(response.data.pagination.currentPage);
            }
        } catch (error) {
            console.error('Error loading partners:', error);
        }
    };

    const loadUsers = async (page = 1) => {
        try {
            const response = await axios.get(`${API_URL}admin/users`, {
                params: {
                    search: searchText,
                    page,
                    pageSize
                }
            });
            if (response.data.success) {
                setUsers(response.data.data);
                setTotalRecords(response.data.pagination.totalCount);
                setCurrentPage(response.data.pagination.currentPage);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadPayments = async (page = 1) => {
        try {
            const response = await axios.get(`${API_URL}admin/payments`, {
                params: {
                    status: paymentStatus,
                    page,
                    pageSize
                }
            });
            if (response.data.success) {
                setPayments(response.data.data);
                setTotalRecords(response.data.pagination.totalCount);
                setCurrentPage(response.data.pagination.currentPage);
            }
        } catch (error) {
            console.error('Error loading payments:', error);
        }
    };

    const loadPartnerPayments = async (page = 1) => {
        try {
            const response = await axios.get(`${API_URL}admin/partner-payments`, {
                params: {
                    status: paymentStatus,
                    page,
                    pageSize
                }
            });
            if (response.data.success) {
                setPartnerPayments(response.data.data);
                setTotalRecords(response.data.pagination.totalCount);
                setCurrentPage(response.data.pagination.currentPage);
            }
        } catch (error) {
            console.error('Error loading partner payments:', error);
        }
    };

    const loadStaff = async (page = 1) => {
        try {
            const response = await axios.get(`${API_URL}admin/staff`, {
                params: {
                    role: staffRole,
                    page,
                    pageSize
                }
            });
            if (response.data.success) {
                setStaff(response.data.data);
                setTotalRecords(response.data.pagination.totalCount);
                setCurrentPage(response.data.pagination.currentPage);
            }
        } catch (error) {
            console.error('Error loading staff:', error);
        }
    };

    const loadNotifications = async () => {
        try {
            const response = await axios.get(`${API_URL}admin/notifications?unreadOnly=true&pageSize=10`);
            if (response.data.success) {
                setNotifications(response.data.data);
            }
        } catch (error) {
            console.error('Error loading notifications:', error);
        }
    };

    const updateCharts = () => {
        if (stats?.charts?.bookingsByStatus) {
            setStatusChartData({
                labels: stats.charts.bookingsByStatus.map(b => b.status),
                datasets: [{
                    data: stats.charts.bookingsByStatus.map(b => b.count),
                    backgroundColor: [
                        '#FF6384',
                        '#36A2EB',
                        '#FFCE56',
                        '#4BC0C0',
                        '#9966FF',
                        '#FF9F40'
                    ],
                    borderWidth: 0
                }]
            });
        }

        if (stats?.charts?.revenueOverTime && stats.charts.revenueOverTime.length > 0) {
            setRevenueChartData({
                labels: stats.charts.revenueOverTime.map(r => r.date),
                datasets: [{
                    label: 'Revenue',
                    data: stats.charts.revenueOverTime.map(r => r.amount),
                    fill: false,
                    borderColor: '#42A5F5',
                    tension: 0.4
                }]
            });
        }
    };

    const markNotificationAsRead = async (id) => {
        try {
            await axios.post(`${API_URL}admin/notifications/${id}/read`);
            loadNotifications();
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllNotificationsAsRead = async () => {
        try {
            await axios.post(`${API_URL}admin/notifications/read-all`);
            loadNotifications();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const getStatusSeverity = (status) => {
        const statusMap = {
            'Pending': 'warning',
            'Processing': 'info',
            'PickedUp': 'info',
            'Completed': 'success',
            'Cancelled': 'danger',
            'Active': 'success',
            'Inactive': 'danger',
            'Suspended': 'warning'
        };
        return statusMap[status] || 'secondary';
    };

    const getPaymentSeverity = (status) => {
        const statusMap = {
            'Pending': 'warning',
            'Processing': 'info',
            'Paid': 'success',
            'Failed': 'danger',
            'Refunded': 'info'
        };
        return statusMap[status] || 'secondary';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(dateString);
    };

 const renderSummaryCards = () => (
    <div className="grid">
        <div className="col-12 md:col-6 lg:col-3">
            <Card className="summary-card" style={{ 
                background: 'var(--surface-card)', 
                borderRadius: '12px', 
                boxShadow: 'var(--card-shadow)',
                border: '1px solid var(--surface-border)'
            }}>
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <span className="text-600 font-medium" style={{ color: 'var(--text-color-secondary)' }}>Total Bookings</span>
                        <div className="text-3xl font-bold mt-2" style={{ color: 'var(--primary-color)' }}>
                            {stats?.summary?.totalBookings || 0}
                        </div>
                        <div className="text-sm mt-2">
                            <span className="font-medium" style={{ color: 'var(--green-500)' }}>
                                +{stats?.summary?.todayBookings || 0}
                            </span>{' '}
                            <span style={{ color: 'var(--text-color-secondary)' }}>today</span>
                        </div>
                    </div>
                    <div className="border-circle p-3" style={{ background: 'var(--primary-100)' }}>
                        <i className="pi pi-shopping-cart text-2xl" style={{ color: 'var(--primary-color)' }}></i>
                    </div>
                </div>
            </Card>
        </div>
        
        <div className="col-12 md:col-6 lg:col-3">
            <Card className="summary-card" style={{ 
                background: 'var(--surface-card)', 
                borderRadius: '12px', 
                boxShadow: 'var(--card-shadow)',
                border: '1px solid var(--surface-border)'
            }}>
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <span className="text-600 font-medium" style={{ color: 'var(--text-color-secondary)' }}>Total Revenue</span>
                        <div className="text-3xl font-bold mt-2" style={{ color: 'var(--green-600)' }}>
                            {formatCurrency(stats?.summary?.totalRevenue || 0)}
                        </div>
                        <div className="text-sm mt-2">
                            <span className="font-medium" style={{ color: 'var(--green-500)' }}>
                                {formatCurrency(stats?.summary?.todayRevenue || 0)}
                            </span>{' '}
                            <span style={{ color: 'var(--text-color-secondary)' }}>today</span>
                        </div>
                    </div>
                    <div className="border-circle p-3" style={{ background: 'var(--green-100)' }}>
                        <i className="pi pi-dollar text-2xl" style={{ color: 'var(--green-600)' }}></i>
                    </div>
                </div>
            </Card>
        </div>
        
        <div className="col-12 md:col-6 lg:col-3">
            <Card className="summary-card" style={{ 
                background: 'var(--surface-card)', 
                borderRadius: '12px', 
                boxShadow: 'var(--card-shadow)',
                border: '1px solid var(--surface-border)'
            }}>
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <span className="text-600 font-medium" style={{ color: 'var(--text-color-secondary)' }}>Active Partners</span>
                        <div className="text-3xl font-bold mt-2" style={{ color: 'var(--purple-600)' }}>
                            {stats?.summary?.activePartners || 0}
                        </div>
                        <div className="text-sm mt-2">
                            <span className="font-medium" style={{ color: 'var(--orange-500)' }}>
                                {stats?.summary?.pendingPartners || 0}
                            </span>{' '}
                            <span style={{ color: 'var(--text-color-secondary)' }}>pending approval</span>
                        </div>
                    </div>
                    <div className="border-circle p-3" style={{ background: 'var(--purple-100)' }}>
                        <i className="pi pi-users text-2xl" style={{ color: 'var(--purple-600)' }}></i>
                    </div>
                </div>
            </Card>
        </div>
        
        <div className="col-12 md:col-6 lg:col-3">
            <Card className="summary-card" style={{ 
                background: 'var(--surface-card)', 
                borderRadius: '12px', 
                boxShadow: 'var(--card-shadow)',
                border: '1px solid var(--surface-border)'
            }}>
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <span className="text-600 font-medium" style={{ color: 'var(--text-color-secondary)' }}>Total Customers</span>
                        <div className="text-3xl font-bold mt-2" style={{ color: 'var(--orange-600)' }}>
                            {stats?.summary?.totalCustomers || 0}
                        </div>
                        <div className="text-sm mt-2">
                            <span className="font-medium" style={{ color: 'var(--red-500)' }}>
                                {stats?.summary?.pendingBookings || 0}
                            </span>{' '}
                            <span style={{ color: 'var(--text-color-secondary)' }}>pending bookings</span>
                        </div>
                    </div>
                    <div className="border-circle p-3" style={{ background: 'var(--orange-100)' }}>
                        <i className="pi pi-user text-2xl" style={{ color: 'var(--orange-600)' }}></i>
                    </div>
                </div>
            </Card>
        </div>
    </div>
);

const renderCharts = () => {
    const prepareStatusChartData = () => {
        if (!stats?.charts?.bookingsByStatus || stats.charts.bookingsByStatus.length === 0) {
            return null;
        }

        const statusColors = {
            'Pending': '#FFCE56',
            'Processing': '#36A2EB',
            'PickedUp': '#9966FF',
            'Completed': '#4BC0C0',
            'Cancelled': '#FF6384'
        };

        return {
            labels: stats.charts.bookingsByStatus.map(item => item.status),
            datasets: [{
                data: stats.charts.bookingsByStatus.map(item => item.count),
                backgroundColor: stats.charts.bookingsByStatus.map(item => 
                    statusColors[item.status] || '#999999'
                ),
                borderWidth: 0,
                hoverOffset: 4
            }]
        };
    };

    const prepareRevenueChartData = () => {
        if (!stats?.charts?.revenueOverTime || stats.charts.revenueOverTime.length === 0) {
            return null;
        }

        return {
            labels: stats.charts.revenueOverTime.map(item => item.date),
            datasets: [{
                label: 'Revenue',
                data: stats.charts.revenueOverTime.map(item => item.amount),
                fill: true,
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                borderColor: 'var(--primary-color)',
                tension: 0.4,
                pointBackgroundColor: 'var(--surface-card)',
                pointBorderColor: 'var(--primary-color)',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverBackgroundColor: 'var(--primary-color)',
                pointHoverBorderColor: 'var(--surface-card)',
                pointHoverRadius: 6
            }]
        };
    };

    const statusChartData = prepareStatusChartData();
    const revenueChartData = prepareRevenueChartData();

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    color: 'var(--text-color)',
                    font: {
                        size: 12
                    }
                }
            },
            tooltip: {
                backgroundColor: 'var(--surface-overlay)',
                titleColor: 'var(--text-color)',
                bodyColor: 'var(--text-color-secondary)',
                borderColor: 'var(--surface-border)',
                borderWidth: 1,
                padding: 10,
                cornerRadius: 4,
                boxPadding: 5,
                usePointStyle: true
            }
        },
        cutout: '60%'
    };

    return (
        <div className="grid mt-4">
            <div className="col-12 lg:col-6">
                <Card 
                    title={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-chart-pie" style={{ color: 'var(--primary-color)' }}></i>
                            <span className="font-semibold" style={{ color: 'var(--text-color)' }}>Bookings by Status</span>
                        </div>
                    }
                    style={{ 
                        background: 'var(--surface-card)', 
                        borderRadius: '12px', 
                        boxShadow: 'var(--card-shadow)',
                        border: '1px solid var(--surface-border)'
                    }}
                >
                    {statusChartData ? (
                        <Chart 
                            type="doughnut" 
                            data={statusChartData} 
                            options={chartOptions}
                            style={{ height: '300px' }}
                        />
                    ) : (
                        <div className="flex flex-column align-items-center justify-content-center p-4" style={{ height: '300px', color: 'var(--text-color-secondary)' }}>
                            <i className="pi pi-chart-pie text-4xl mb-3" style={{ color: 'var(--text-color-secondary)' }}></i>
                            <p>No booking data available</p>
                        </div>
                    )}
                </Card>
            </div>
            
            <div className="col-12 lg:col-6">
                <Card 
                    title={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-chart-line" style={{ color: 'var(--primary-color)' }}></i>
                            <span className="font-semibold" style={{ color: 'var(--text-color)' }}>Revenue Over Time</span>
                        </div>
                    }
                    style={{ 
                        background: 'var(--surface-card)', 
                        borderRadius: '12px', 
                        boxShadow: 'var(--card-shadow)',
                        border: '1px solid var(--surface-border)'
                    }}
                >
                    {revenueChartData ? (
                        <Chart 
                            type="line" 
                            data={revenueChartData} 
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: false
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => {
                                                return `Revenue: ${formatCurrency(context.raw)}`;
                                            }
                                        },
                                        backgroundColor: 'var(--surface-overlay)',
                                        titleColor: 'var(--text-color)',
                                        bodyColor: 'var(--text-color-secondary)',
                                        borderColor: 'var(--surface-border)',
                                        borderWidth: 1,
                                        padding: 10,
                                        cornerRadius: 4
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        grid: {
                                            color: 'var(--surface-border)',
                                            drawBorder: false
                                        },
                                        ticks: {
                                            callback: (value) => formatCurrency(value),
                                            color: 'var(--text-color-secondary)'
                                        }
                                    },
                                    x: {
                                        grid: {
                                            display: false
                                        },
                                        ticks: {
                                            color: 'var(--text-color-secondary)',
                                            maxRotation: 45,
                                            minRotation: 45
                                        }
                                    }
                                }
                            }}
                            style={{ height: '300px' }}
                        />
                    ) : (
                        <div className="flex flex-column align-items-center justify-content-center p-4" style={{ height: '300px', color: 'var(--text-color-secondary)' }}>
                            <i className="pi pi-chart-line text-4xl mb-3" style={{ color: 'var(--text-color-secondary)' }}></i>
                            <p>No revenue data available for the selected period</p>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};


    const renderTopPartners = () => (
        <Card title="Top Performing Partners" className="mt-4">
            {stats?.topPartners && stats.topPartners.length > 0 ? (
                <DataTable 
                    value={stats.topPartners}
                    size="small"
                    emptyMessage="No data available"
                >
                    <Column field="partnerName" header="Partner" />
                    <Column field="completedBookings" header="Completed Bookings" />
                    <Column 
                        field="totalRevenue" 
                        header="Revenue" 
                        body={(row) => formatCurrency(row.totalRevenue)}
                    />
                    <Column 
                        field="avgRating" 
                        header="Avg Rating" 
                        body={(row) => (
                            <div className="flex align-items-center">
                                <span>{row.avgRating ? row.avgRating.toFixed(1) : '0.0'}</span>
                                <i className="pi pi-star-fill text-yellow-500 ml-1 text-sm"></i>
                            </div>
                        )}
                    />
                </DataTable>
            ) : (
                <div className="text-center p-4 text-gray-500">
                    <i className="pi pi-users text-3xl mb-2"></i>
                    <p>No partner data available</p>
                </div>
            )}
        </Card>
    );

    const renderNotificationsPanel = () => (
        <Card title="Notifications" className="h-full">
            <style>{`
                .notification-item {
                    transition: all 0.2s ease;
                    border-left: 3px solid transparent;
                }
                .notification-item.unread {
                    border-left-color: var(--primary-color);
                    background: linear-gradient(to right, rgba(33, 150, 243, 0.05), transparent);
                }
                .notification-item:hover {
                    background: var(--surface-100) !important;
                    transform: translateX(2px);
                }
            `}</style>
            
            <div className="flex justify-content-between align-items-center mb-3">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-bell text-primary" style={{ fontSize: '1.2rem' }}></i>
                    <span className="font-semibold">Latest Updates</span>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button 
                        icon="pi pi-check"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={markAllNotificationsAsRead}
                        disabled={notifications.filter(n => !n.isRead).length === 0}
                        tooltip="Mark all as read"
                        tooltipOptions={{ position: 'left' }}
                    />
                    <Badge 
                        value={notifications.filter(n => !n.isRead).length} 
                        severity="danger" 
                    />
                </div>
            </div>
            
            {notifications.length === 0 ? (
                <div className="flex flex-column align-items-center justify-content-center p-4" style={{ minHeight: '200px' }}>
                    <i className="pi pi-bell-slash text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500">No new notifications</p>
                </div>
            ) : (
                <div className="flex flex-column gap-2">
                    {notifications.map(notification => (
                        <div 
                            key={notification.id}
                            className={`notification-item p-3 border-round surface-card cursor-pointer 
                                ${!notification.isRead ? 'unread' : ''}`}
                            onClick={() => markNotificationAsRead(notification.id)}
                            style={{ 
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                borderBottom: '1px solid var(--surface-200)'
                            }}
                        >
                            <div className="flex align-items-start gap-2">
                                <div className="flex-grow-1">
                                    <div className="flex justify-content-between align-items-start">
                                        <span className="font-semibold text-900">{notification.title}</span>
                                        <small className="text-500 ml-2">
                                            {formatRelativeTime(notification.sentAt)}
                                        </small>
                                    </div>
                                    <p className="text-sm text-600 m-0 mt-1">{notification.message}</p>
                                </div>
                                {!notification.isRead && (
                                    <span 
                                        className="bg-primary border-circle"
                                        style={{ width: '8px', height: '8px', marginTop: '8px' }}
                                    ></span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            {notifications.length > 0 && (
                <div className="mt-3 text-center border-top-1 surface-200 pt-3">
                    <Button 
                        label="View All"
                        icon="pi pi-arrow-right"
                        className="p-button-text p-button-sm"
                        onClick={() => window.location.href = '/admin/notifications'}
                    />
                </div>
            )}
        </Card>
    );

    const renderRecentBookings = () => (
        <Card title="Recent Bookings" className="mt-4">
            <DataTable 
                value={recentBookings}
                size="small"
                emptyMessage="No recent bookings"
            >
                <Column field="bookingreference" header="Reference" />
                <Column field="customer.fullname" header="Customer" />
                <Column field="partner.shopname" header="Partner" />
                <Column 
                    field="status" 
                    header="Status" 
                    body={(row) => (
                        <Badge 
                            value={row.status} 
                            severity={getStatusSeverity(row.status)}
                        />
                    )}
                />
                <Column 
                    field="paymentstatus" 
                    header="Payment" 
                    body={(row) => (
                        <Badge 
                            value={row.paymentStatus} 
                            severity={getPaymentSeverity(row.paymentstatus)}
                        />
                    )}
                />
                <Column 
                    field="totalamount" 
                    header="Amount" 
                    body={(row) => formatCurrency(row.totalamount)}
                />
                <Column 
                    field="createdat" 
                    header="Created" 
                    body={(row) => formatRelativeTime(row.createdat)}
                />
            </DataTable>
        </Card>
    );

    const renderPartnersTab = () => (
        <div className="p-3">
            <div className="flex justify-content-between align-items-center mb-3">
                <div className="flex gap-2">
                    <Dropdown 
                        value={partnerStatus}
                        options={[
                            { label: 'All Status', value: null },
                            { label: 'Active', value: 'Active' },
                            { label: 'Pending', value: 'Pending' },
                            { label: 'Inactive', value: 'Inactive' },
                            { label: 'Suspended', value: 'Suspended' }
                        ]}
                        onChange={(e) => {
                            setPartnerStatus(e.value);
                            loadPartners(1);
                        }}
                        placeholder="Filter by Status"
                        className="w-full md:w-14rem"
                    />
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText 
                            placeholder="Search partners..." 
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && loadPartners(1)}
                            className="w-full md:w-20rem"
                        />
                    </span>
                </div>
                <Button 
                    label="Add Partner"
                    icon="pi pi-plus"
                    onClick={() => {/* Navigate to add partner */}}
                />
            </div>
            
            <DataTable 
                value={partners}
                paginator 
                rows={pageSize}
                totalRecords={totalRecords}
                first={(currentPage - 1) * pageSize}
                onPage={(e) => loadPartners(e.page + 1)}
                loading={loading}
                emptyMessage="No partners found"
                size="small"
            >
                <Column field="shopcode" header="Code" />
                <Column field="shopname" header="Shop Name" />
                <Column field="ownername" header="Owner" />
                <Column field="contactphone" header="Phone" />
                <Column field="city" header="City" />
                <Column 
                    field="status" 
                    header="Status" 
                    body={(row) => (
                        <Badge 
                            value={row.status} 
                            severity={getStatusSeverity(row.status)}
                        />
                    )}
                />
                <Column 
                    field="isverified" 
                    header="Verified" 
                    body={(row) => row.isVerified ? 'Yes' : 'No'}
                />
                <Column field="rating" header="Rating" />
                <Column 
                    field="currentload" 
                    header="Load" 
                    body={(row) => `${row.currentload}/${row.capacityperday}`}
                />
                <Column 
                    header="Actions" 
                    body={(row) => (
                        <Button 
                            icon="pi pi-eye"
                            className="p-button-rounded p-button-text p-button-sm"
                            onClick={() => {
                                setSelectedItem(row);
                                setShowPartnerDialog(true);
                            }}
                        />
                    )}
                    style={{ width: '80px' }}
                />
            </DataTable>
        </div>
    );

    const renderUsersTab = () => (
        <div className="p-3">
            <div className="flex justify-content-between align-items-center mb-3">
                <div className="flex gap-2">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText 
                            placeholder="Search users..." 
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && loadUsers(1)}
                            className="w-full md:w-20rem"
                        />
                    </span>
                </div>
            </div>
            
            <DataTable 
                value={users}
                paginator 
                rows={pageSize}
                totalRecords={totalRecords}
                first={(currentPage - 1) * pageSize}
                onPage={(e) => loadUsers(e.page + 1)}
                loading={loading}
                emptyMessage="No users found"
                size="small"
            >
                <Column field="fullname" header="Name" />
                <Column field="email" header="Email" />
                <Column field="phonenumber" header="Phone" />
                <Column field="city" header="City" />
                <Column 
                    field="isactive" 
                    header="Status" 
                    body={(row) => (
                        <Badge 
                            value={row.isactive ? 'Active' : 'Inactive'} 
                            severity={row.isactive ? 'success' : 'danger'}
                        />
                    )}
                />
                <Column 
                    field="isAdmin" 
                    header="Role" 
                    body={(row) => row.isadmin ? 'Admin' : 'Customer'}
                />
                <Column field="loyaltyPoints" header="Points" />
                <Column 
                    field="totalbookings" 
                    header="Bookings" 
                />
                <Column 
                    field="totalspent" 
                    header="Total Spent" 
                    body={(row) => formatCurrency(row.totalSpent)}
                />
                <Column 
                    field="registrationdate" 
                    header="Joined" 
                    body={(row) => formatDate(row.registrationDate)}
                />
                <Column 
                    header="Actions" 
                    body={(row) => (
                        <Button 
                            icon="pi pi-eye"
                            className="p-button-rounded p-button-text p-button-sm"
                            onClick={() => {
                                setSelectedItem(row);
                                setShowUserDialog(true);
                            }}
                        />
                    )}
                    style={{ width: '80px' }}
                />
            </DataTable>
        </div>
    );

    const renderPaymentsTab = () => (
        <div className="p-3">
            <div className="flex gap-2 mb-3">
                <Dropdown 
                    value={paymentStatus}
                    options={[
                        { label: 'All Status', value: null },
                        { label: 'Pending', value: 'Pending' },
                        { label: 'Processing', value: 'Processing' },
                        { label: 'Paid', value: 'Paid' },
                        { label: 'Failed', value: 'Failed' },
                        { label: 'Refunded', value: 'Refunded' }
                    ]}
                    onChange={(e) => {
                        setPaymentStatus(e.value);
                        loadPayments(1);
                    }}
                    placeholder="Filter by Status"
                    className="w-full md:w-14rem"
                />
            </div>
            
            <DataTable 
                value={payments}
                paginator 
                rows={pageSize}
                totalRecords={totalRecords}
                first={(currentPage - 1) * pageSize}
                onPage={(e) => loadPayments(e.page + 1)}
                loading={loading}
                emptyMessage="No payments found"
                size="small"
            >
                <Column field="paymentReference" header="Reference" />
                <Column field="user.fullName" header="User" />
                <Column field="booking.bookingReference" header="Booking" />
                <Column 
                    field="amount" 
                    header="Amount" 
                    body={(row) => formatCurrency(row.amount)}
                />
                <Column field="paymentMethod" header="Method" />
                <Column 
                    field="status" 
                    header="Status" 
                    body={(row) => (
                        <Badge 
                            value={row.status} 
                            severity={getPaymentSeverity(row.status)}
                        />
                    )}
                />
                <Column 
                    field="paidAt" 
                    header="Paid Date" 
                    body={(row) => formatDate(row.paidAt)}
                />
                <Column 
                    header="Actions" 
                    body={(row) => (
                        <Button 
                            icon="pi pi-eye"
                            className="p-button-rounded p-button-text p-button-sm"
                            onClick={() => {
                                setSelectedItem(row);
                                setShowPaymentDialog(true);
                            }}
                        />
                    )}
                    style={{ width: '80px' }}
                />
            </DataTable>
        </div>
    );

    const renderPartnerPaymentsTab = () => (
        <div className="p-3">
            <div className="flex gap-2 mb-3">
                <Dropdown 
                    value={paymentStatus}
                    options={[
                        { label: 'All Status', value: null },
                        { label: 'Pending', value: 'Pending' },
                        { label: 'Processing', value: 'Processing' },
                        { label: 'Paid', value: 'Paid' },
                        { label: 'Failed', value: 'Failed' }
                    ]}
                    onChange={(e) => {
                        setPaymentStatus(e.value);
                        loadPartnerPayments(1);
                    }}
                    placeholder="Filter by Status"
                    className="w-full md:w-14rem"
                />
            </div>
            
            <DataTable 
                value={partnerPayments}
                paginator 
                rows={pageSize}
                totalRecords={totalRecords}
                first={(currentPage - 1) * pageSize}
                onPage={(e) => loadPartnerPayments(e.page + 1)}
                loading={loading}
                emptyMessage="No partner payments found"
                size="small"
            >
                <Column field="paymentreference" header="Reference" />
                <Column field="partnerShop.shopname" header="Partner" />
                <Column field="periodStart" header="Period Start" body={(row) => formatDate(row.periodstart)} />
                <Column field="periodEnd" header="Period End" body={(row) => formatDate(row.periodend)} />
                <Column field="totalBookings" header="Bookings" />
                <Column 
                    field="totalamount" 
                    header="Total" 
                    body={(row) => formatCurrency(row.totalamount)}
                />
                <Column 
                    field="commissionamount" 
                    header="Commission" 
                    body={(row) => formatCurrency(row.commissionamount)}
                />
                <Column 
                    field="partnerearnings" 
                    header="Earnings" 
                    body={(row) => formatCurrency(row.partnerearnings)}
                />
                <Column 
                    field="status" 
                    header="Status" 
                    body={(row) => (
                        <Badge 
                            value={row.status} 
                            severity={getPaymentSeverity(row.status)}
                        />
                    )}
                />
            </DataTable>
        </div>
    );

    const renderStaffTab = () => (
        <div className="p-3">
            <div className="flex justify-content-between align-items-center mb-3">
                <div className="flex gap-2">
                    <Dropdown 
                        value={staffRole}
                        options={[
                            { label: 'All Roles', value: null },
                            { label: 'Driver', value: 'Driver' },
                            { label: 'Processor', value: 'Processor' },
                            { label: 'Manager', value: 'Manager' },
                            { label: 'Admin', value: 'Admin' }
                        ]}
                        onChange={(e) => {
                            setStaffRole(e.value);
                            loadStaff(1);
                        }}
                        placeholder="Filter by Role"
                        className="w-full md:w-14rem"
                    />
                </div>
                <Button 
                    label="Add Staff"
                    icon="pi pi-plus"
                    onClick={() => {/* Navigate to add staff */}}
                />
            </div>
            
            <DataTable 
                value={staff}
                paginator 
                rows={pageSize}
                totalRecords={totalRecords}
                first={(currentPage - 1) * pageSize}
                onPage={(e) => loadStaff(e.page + 1)}
                loading={loading}
                emptyMessage="No staff found"
                size="small"
            >
                <Column field="employeeid" header="Emp ID" />
                <Column field="fullname" header="Name" />
                <Column field="email" header="Email" />
                <Column field="phonenumber" header="Phone" />
                <Column field="role" header="Role" />
                <Column field="department" header="Dept" />
                <Column 
                    field="status" 
                    header="Status" 
                    body={(row) => (
                        <Badge 
                            value={row.status} 
                            severity={getStatusSeverity(row.status)}
                        />
                    )}
                />
                <Column 
                    field="availabilitystatus" 
                    header="Availability" 
                    body={(row) => (
                        <Badge 
                            value={row.availabilitystatus} 
                            severity={row.availabilitystatus === 'Available' ? 'success' : 'warning'}
                        />
                    )}
                />
                <Column field="vehicleType" header="Vehicle" />
                <Column 
                    header="Actions" 
                    body={(row) => (
                        <Button 
                            icon="pi pi-eye"
                            className="p-button-rounded p-button-text p-button-sm"
                            onClick={() => {
                                setSelectedItem(row);
                                /* Show staff details */
                            }}
                        />
                    )}
                    style={{ width: '80px' }}
                />
            </DataTable>
        </div>
    );

    return (
        <div className="admin-dashboard p-4">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="text-3xl font-bold m-0">Admin Dashboard</h1>
                <div className="flex gap-2">
                    <Calendar 
                        value={dateRange} 
                        onChange={(e) => setDateRange(e.value)}
                        selectionMode="range" 
                        placeholder="Select Date Range"
                        showButtonBar
                        className="w-full md:w-20rem"
                    />
                    <Button 
                        label="Apply Filter"
                        icon="pi pi-filter"
                        onClick={loadDashboardData}
                    />
                    <Button 
                        label="Refresh"
                        icon="pi pi-refresh"
                        className="p-button-outlined"
                        onClick={() => {
                            loadDashboardData();
                            loadRecentBookings();
                            loadNotifications();
                        }}
                    />
                </div>
            </div>
            
            {loading && !stats ? (
                <div className="text-center p-6">
                    <ProgressBar mode="indeterminate" style={{ width: '50px', height: '50px' }} />
                    <p className="mt-3">Loading dashboard...</p>
                </div>
            ) : (
                <>
                    {renderSummaryCards()}
                    
                    <div className="grid mt-4">
                        <div className="col-12 lg:col-8">
                            {renderCharts()}
                            
                            <div className="mt-4">
                                {renderTopPartners()}
                            </div>
                            
                            <div className="mt-4">
                                {renderRecentBookings()}
                            </div>
                        </div>
                        
                        <div className="col-12 lg:col-4">
                            {renderNotificationsPanel()}
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <Card>
                            <TabView>
                                <TabPanel header="Partners">
                                    {renderPartnersTab()}
                                </TabPanel>
                                <TabPanel header="Users">
                                    {renderUsersTab()}
                                </TabPanel>
                                <TabPanel header="Payments">
                                    {renderPaymentsTab()}
                                </TabPanel>
                                <TabPanel header="Partner Payments">
                                    {renderPartnerPaymentsTab()}
                                </TabPanel>
                                <TabPanel header="Staff">
                                    {renderStaffTab()}
                                </TabPanel>
                            </TabView>
                        </Card>
                    </div>
                </>
            )}
            
            {/* Dialogs */}
            <Dialog 
                header="Partner Details" 
                visible={showPartnerDialog} 
                style={{ width: '600px' }}
                onHide={() => setShowPartnerDialog(false)}
            >
                {selectedItem && (
                    <div className="p-3">
                        <h3>{selectedItem.shopName}</h3>
                        <div className="grid">
                            <div className="col-6">
                                <p><strong>Code:</strong> {selectedItem.shopcode}</p>
                                <p><strong>Owner:</strong> {selectedItem.ownername}</p>
                                <p><strong>Phone:</strong> {selectedItem.contactphone}</p>
                                <p><strong>Email:</strong> {selectedItem.contactemail}</p>
                            </div>
                            <div className="col-6">
                                <p><strong>City:</strong> {selectedItem.city}</p>
                                <p><strong>State:</strong> {selectedItem.state}</p>
                                <p><strong>Status:</strong> {selectedItem.status}</p>
                                <p><strong>Commission:</strong> {selectedItem.commissionrate}%</p>
                            </div>
                        </div>
                        <div className="mt-3">
                            <p><strong>Rating:</strong> {selectedItem.rating} ({selectedItem.totalreviews} reviews)</p>
                            <p><strong>Load:</strong> {selectedItem.currentLoad}/{selectedItem.capacityperday}</p>
                            <p><strong>Total Bookings:</strong> {selectedItem.totalbookings}</p>
                            <p><strong>Completed:</strong> {selectedItem.completedbookings}</p>
                            <p><strong>Revenue:</strong> {formatCurrency(selectedItem.revenue)}</p>
                        </div>
                    </div>
                )}
            </Dialog>
            
            <Dialog 
                header="User Details" 
                visible={showUserDialog} 
                style={{ width: '600px' }}
                onHide={() => setShowUserDialog(false)}
            >
                {selectedItem && (
                    <div className="p-3">
                        <h3>{selectedItem.fullName}</h3>
                        <div className="grid">
                            <div className="col-6">
                                <p><strong>Email:</strong> {selectedItem.email}</p>
                                <p><strong>Phone:</strong> {selectedItem.phonenumber}</p>
                                <p><strong>City:</strong> {selectedItem.city}</p>
                                <p><strong>State:</strong> {selectedItem.state}</p>
                            </div>
                            <div className="col-6">
                                <p><strong>Joined:</strong> {formatDate(selectedItem.registrationdate)}</p>
                                <p><strong>Last Login:</strong> {formatDate(selectedItem.lastlogin)}</p>
                                <p><strong>Loyalty Points:</strong> {selectedItem.loyaltypoints}</p>
                                <p><strong>Total Spent:</strong> {formatCurrency(selectedItem.totalspent)}</p>
                            </div>
                        </div>
                        <div className="mt-3">
                            <p><strong>Total Bookings:</strong> {selectedItem.totalbookings}</p>
                            <p><strong>Email Verified:</strong> {selectedItem.isemailverified ? 'Yes' : 'No'}</p>
                            <p><strong>Phone Verified:</strong> {selectedItem.isphoneverified ? 'Yes' : 'No'}</p>
                            <p><strong>Admin:</strong> {selectedItem.isadmin ? 'Yes' : 'No'}</p>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default AdminDashboard;